import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, Package, DollarSign, Star, X, CreditCard, Lock, Eye, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import logger from '../utils/logger';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import BrandLogo from '../components/BrandLogo';

const DriverDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'offers'
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerStatistics, setOfferStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRequests: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [offerPagination, setOfferPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalOffers: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [pageSize, setPageSize] = useState(10);
  const [offerPageSize, setOfferPageSize] = useState(10);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const navigate = useNavigate();
  const { user, subscription, enableTrialPeriod, logout, token } = useAuth();

  const fetchDashboardData = useCallback(async (page = 1, limit = pageSize) => {
    try {
      const [requestsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.REQUESTS}/available?page=${page}&limit=${limit}`),
        axios.get(`${API_ENDPOINTS.DRIVERS}/dashboard`)
      ]);

      setRequests(requestsRes.data.data);
      setPagination(requestsRes.data.pagination);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      // Check if it's a subscription error
      if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        setShowPaywallModal(true);
      } else {
        toast.error('שגיאה בטעינת נתוני הדשבורד');
      }
      setLoading(false);
    }
  }, [pageSize]);

  const fetchOffersData = useCallback(async (page = 1, limit = offerPageSize) => {
    try {
      const [offersRes, statsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.OFFERS}/driver?page=${page}&limit=${limit}`),
        axios.get(`${API_ENDPOINTS.OFFERS}/driver/statistics`)
      ]);

      setOffers(offersRes.data.data);
      setOfferPagination(offersRes.data.pagination);
      setOfferStatistics(statsRes.data.data);
    } catch (error) {
      logger.error('Error fetching offers data:', error);
      // Check if it's a subscription error
      if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        setShowPaywallModal(true);
      } else {
        toast.error('שגיאה בטעינת נתוני ההצעות');
      }
    }
  }, [offerPageSize]);

  useEffect(() => {
    // Only fetch data if we have a token (user is authenticated)
    // This prevents 401 errors on initial load when token might not be set yet
    if (user && token) {
      fetchDashboardData();
      fetchOffersData(); // Always fetch offers data to check for existing offers
    }
  }, [fetchDashboardData, fetchOffersData, user, token]);

  const submitOffer = async (requestId, price, notes) => {
    try {
      // Check if driver already has an offer for this request
      const existingOffer = offers.find(offer => offer.requestId === requestId);
      if (existingOffer) {
        toast.error('כבר הגשת הצעה על בקשה זו');
        return;
      }

      await axios.post(`${API_ENDPOINTS.OFFERS}/create`, {
        requestId,
        price: parseFloat(price),
        notes
      });
      
      toast.success('ההצעה נשלחה בהצלחה!');
      // Refresh both tabs to show the new offer
      fetchDashboardData(pagination.currentPage);
      fetchOffersData(offerPagination.currentPage);
    } catch (error) {
      if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        setShowPaywallModal(true);
      } else {
        toast.error(error.response?.data?.message || 'שגיאה בשליחת ההצעה');
      }
    }
  };


  const cancelOffer = async (offerId) => {
    try {
      await axios.post(`${API_ENDPOINTS.OFFERS}/${offerId}/withdraw`);
      
      toast.success('ההצעה בוטלה בהצלחה!');
      // Refresh offers data
      fetchOffersData(offerPagination.currentPage);
    } catch (error) {
      if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        setShowPaywallModal(true);
      } else {
        toast.error(error.response?.data?.message || 'שגיאה בביטול ההצעה');
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/driver/login');
  };

  const handleRefresh = async () => {
    const toastId = toast.loading('מרענן נתונים...');
    try {
      await Promise.all([
        fetchDashboardData(pagination.currentPage),
        fetchOffersData(offerPagination.currentPage)
      ]);
      toast.success('הנתונים עודכנו', { id: toastId });
    } catch (error) {
      toast.error('שגיאה ברענון הנתונים', { id: toastId });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header id="navigation" className="header-gradient shadow-lg" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pl-24 sm:pl-28 md:pl-14">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <BrandLogo className="h-8 md:h-10 w-auto" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mr-2 md:mr-4">דשבורד מוביל</h1>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              {/* Subscription Status */}
              {subscription && (
                <div className="hidden md:flex items-center space-x-2 space-x-reverse">
                  {enableTrialPeriod && subscription.status === 'TRIAL' && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      ניסיון: {subscription.daysLeft} ימים
                    </span>
                  )}
                  {subscription.status === 'ACTIVE' && (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      מנוי פעיל
                    </span>
                  )}
                  {enableTrialPeriod && subscription.status === 'EXPIRED' && (
                    <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      מנוי פג תוקף
                    </span>
                  )}
                </div>
              )}
              <span className="hidden sm:inline text-white text-base sm:text-lg md:text-xl">שלום, {user?.contactPerson || user?.businessName}</span>
              <button
                onClick={handleLogout}
                className="text-white hover:text-orange-100 font-semibold transition-colors duration-300"
              >
                התנתק
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" tabIndex="-1">
        {/* Trial Banner */}
        {enableTrialPeriod && subscription && (subscription.status === 'TRIAL' || subscription.status === 'EXPIRED') && (
          <div className={`mb-6 rounded-xl p-4 sm:p-5 shadow-lg ${
            subscription.status === 'EXPIRED' 
              ? 'bg-red-50 border border-red-200' 
              : subscription.daysLeft <= 7 
                ? 'bg-yellow-50 border border-yellow-200' 
                : 'bg-blue-50 border border-blue-200'
          }`} role="status" aria-live="polite">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-semibold text-sm sm:text-base ${
                  subscription.status === 'EXPIRED' 
                    ? 'text-red-900' 
                    : subscription.daysLeft <= 7 
                      ? 'text-yellow-900' 
                      : 'text-brown-900'
                }`}>
                  {subscription.status === 'EXPIRED' 
                    ? 'תקופת הניסיון שלך הסתיימה. נדרש מנוי פעיל כדי להמשיך.' 
                    : subscription.daysLeft <= 7 
                      ? `⚠️ תקופת הניסיון שלך מסתיימת בקרוב! נותרו ${subscription.daysLeft} ימים בלבד.`
                      : `אתה בתקופת ניסיון חינמית. נותרו ${subscription.daysLeft} ימים.`
                  }
                </p>
                {subscription.trialEndsAt && (
                  <p className={`text-xs sm:text-sm mt-1 ${
                    subscription.status === 'EXPIRED' 
                      ? 'text-red-700' 
                      : subscription.daysLeft <= 7 
                        ? 'text-yellow-700' 
                        : 'text-brown-700'
                  }`}>
                    תאריך סיום: {new Date(subscription.trialEndsAt).toLocaleDateString('he-IL')}
                  </p>
                )}
              </div>
              <button onClick={() => window.location.assign('/billing') } className="btn-primary px-4 py-2">
                {subscription.status === 'EXPIRED' ? 'הפעל מנוי' : 'נהל מנוי'}
              </button>
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
            <button 
                onClick={handleRefresh}
                className="flex items-center gap-2 text-brown-600 hover:text-orange-600 transition-colors"
            >
                <RefreshCw className="h-5 w-5" />
                <span>רענן נתונים</span>
            </button>
        </div>

        <h2 className="sr-only">סקירה כללית</h2>
        {/* Statistics Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
              <Truck className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-orange-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-2">בקשות זמינות</h3>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-600">{pagination?.totalRequests || 0}</p>
          </div>

          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
              <Package className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-orange-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-2">הצעות שנשלחו</h3>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-600">{offerStatistics?.total || 0}</p>
          </div>

          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-orange-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-2">הכנסה ממוצעת</h3>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-600">
              ₪{offerStatistics?.accepted > 0 ? Math.floor(offerStatistics.totalValue / offerStatistics.accepted) : 0}
            </p>
          </div>

          <div className="card text-center hover-lift">
            <div className="bg-orange-100 p-4 rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
              <Star className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-orange-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-2">דירוג ממוצע</h3>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-600">{user?.rating?.toFixed(1) || 0}</p>
            <p className="text-sm text-brown-600 mt-1">ציון דירוג: {user?.rankingScore?.toFixed(1) || 0}/5</p>
            <p className="text-xs text-brown-500 mt-1">שיעור השלמה: {((user?.completionRate || 0) * 100).toFixed(0)}%</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 space-x-reverse bg-white rounded-xl p-1 shadow-lg">
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-2 md:py-3 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'requests'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-brown-700 hover:text-orange-600'
              }`}
            >
              בקשות זמינות
            </button>
            <button
              onClick={() => setActiveTab('offers')}
              className={`flex-1 py-2 md:py-3 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'offers'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-brown-700 hover:text-orange-600'
              }`}
            >
              ההצעות שלי
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'requests' ? (
          <div className="space-y-6">
            {/* Page Size Selector */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-brown-900">בקשות הובלה זמינות</h2>
              <div className="flex items-center space-x-3 space-x-reverse">
                <label htmlFor="page-size" className="text-brown-700 font-medium text-sm sm:text-base">מספר פריטים בעמוד:</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    fetchDashboardData(1, parseInt(e.target.value));
                  }}
                  className="input-field w-20 text-sm sm:text-base"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Requests List */}
            {requests.length === 0 ? (
              <div className="card text-center py-12 hover-lift">
                <Truck className="h-12 w-12 sm:h-16 sm:w-16 text-brown-300 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-brown-700 mb-2">אין בקשות זמינות</h3>
                <p className="text-brown-600">כרגע אין בקשות הובלה זמינות באזור השירות שלך</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {requests.map((request) => (
                  <RequestCard
                    key={request._id}
                    request={request}
                    onSubmitOffer={submitOffer}
                    hasExistingOffer={offers.some(offer => offer.requestId === request._id)}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 space-x-reverse mt-8">
                <button
                  onClick={() => fetchDashboardData(pagination.currentPage - 1, pageSize)}
                  disabled={!pagination.hasPrevPage}
                  className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הקודם
                </button>
                
                <span className="text-brown-700 font-medium">
                  עמוד {pagination.currentPage} מתוך {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => fetchDashboardData(pagination.currentPage + 1, pageSize)}
                  disabled={!pagination.hasNextPage}
                  className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הבא
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Page Size Selector */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-brown-900">ההצעות שלי</h2>
              <div className="flex items-center space-x-3 space-x-reverse">
                <label htmlFor="offer-page-size" className="text-brown-700 font-medium text-sm sm:text-base">מספר פריטים בעמוד:</label>
                <select
                  id="offer-page-size"
                  value={offerPageSize}
                  onChange={(e) => {
                    setOfferPageSize(parseInt(e.target.value));
                    fetchOffersData(1, parseInt(e.target.value));
                  }}
                  className="input-field w-20 text-sm sm:text-base"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Offers List */}
            {offers.length === 0 ? (
              <div className="card text-center py-12 hover-lift">
                <Package className="h-12 w-12 sm:h-16 sm:w-16 text-brown-300 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-brown-700 mb-2">אין הצעות</h3>
                <p className="text-brown-600">עדיין לא שלחת הצעות על בקשות הובלה</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {offers.map((offer) => (
                  <OfferCard
                    key={offer._id}
                    offer={offer}
                    onCancelOffer={cancelOffer}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {offerPagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 space-x-reverse mt-8">
                <button
                  onClick={() => fetchOffersData(offerPagination.currentPage - 1, offerPageSize)}
                  disabled={!offerPagination.hasPrevPage}
                  className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הקודם
                </button>
                
                <span className="text-brown-700 font-medium">
                  עמוד {offerPagination.currentPage} מתוך {offerPagination.totalPages}
                </span>
                
                <button
                  onClick={() => fetchOffersData(offerPagination.currentPage + 1, offerPageSize)}
                  disabled={!offerPagination.hasNextPage}
                  className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הבא
                </button>
              </div>
            )}
          </div>
        )}

        {/* Paywall Modal */}
        {showPaywallModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <Lock className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-brown-900 mb-4">נדרש מנוי פעיל</h3>
                <p className="text-brown-700 mb-6">
                  תקופת הניסיון שלך הסתיימה. כדי להמשיך להשתמש בשירות, נדרש מנוי חודשי פעיל.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => window.location.assign('/billing')}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <CreditCard className="h-5 w-5 ml-2" />
                    הפעל מנוי
                  </button>
                  <button
                    onClick={() => setShowPaywallModal(false)}
                    className="w-full btn-secondary"
                  >
                    סגור
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Request Details Modal
const RequestDetailsModal = ({ request, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl" dir="rtl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-brown-900">פרטי בקשה מלאים</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
           {/* Left Column: Route Info */}
           <div>
              <h4 className="text-lg font-bold text-orange-600 mb-4 border-b border-orange-100 pb-2">פרטי מסלול</h4>
              <div className="space-y-4">
                  <div>
                      <p className="font-bold text-gray-700">כתובת מוצא</p>
                      <p className="text-gray-900 text-lg">{request.originAddress?.street || request.originAddress}</p>
                      <div className="flex gap-4 mt-1 text-sm text-gray-600">
                          <span>קומה: {request.originAddress?.floor ?? request.originFloor ?? 'לא ידוע'}</span>
                          <span>מעלית: {request.originAddress?.hasElevator ? 'יש' : 'אין'}</span>
                      </div>
                      {request.originAddress?.buildingType && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span>סוג בית: {request.originAddress.buildingType}</span>
                          {request.originAddress?.numberOfRooms !== undefined && (
                            <span className="mr-3"> | חדרים: {request.originAddress.numberOfRooms}</span>
                          )}
                        </div>
                      )}
                      {request.originAddress?.accessPath && request.originAddress.accessPath.length > 0 && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span>שביל גישה: {request.originAddress.accessPath.join(', ')}</span>
                        </div>
                      )}
                  </div>
                  <div>
                      <p className="font-bold text-gray-700">כתובת יעד</p>
                      <p className="text-gray-900 text-lg">{request.destinationAddress?.street || request.destinationAddress}</p>
                      <div className="flex gap-4 mt-1 text-sm text-gray-600">
                          <span>קומה: {request.destinationAddress?.floor ?? request.destinationFloor ?? 'לא ידוע'}</span>
                          <span>מעלית: {request.destinationAddress?.hasElevator ? 'יש' : 'אין'}</span>
                      </div>
                      {request.destinationAddress?.buildingType && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span>סוג בית: {request.destinationAddress.buildingType}</span>
                          {request.destinationAddress?.numberOfRooms !== undefined && (
                            <span className="mr-3"> | חדרים: {request.destinationAddress.numberOfRooms}</span>
                          )}
                        </div>
                      )}
                      {request.destinationAddress?.accessPath && request.destinationAddress.accessPath.length > 0 && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span>שביל גישה: {request.destinationAddress.accessPath.join(', ')}</span>
                        </div>
                      )}
                  </div>
                  <div>
                      <p className="font-bold text-gray-700">תאריך מועדף</p>
                      <p className="text-gray-900">
                        {request.preferredDate ? new Date(request.preferredDate).toLocaleDateString('he-IL') : 'לא ידוע'}
                      </p>
                  </div>
              </div>
           </div>

           {/* Right Column: Contents Info */}
           <div>
              <h4 className="text-lg font-bold text-orange-600 mb-4 border-b border-orange-100 pb-2">תכולה ודרישות</h4>
              <div className="space-y-4">
                  <div>
                      <p className="font-bold text-gray-700">סוג הובלה</p>
                      <p className="text-gray-900">{request.category}</p>
                  </div>
                  <div>
                      <p className="font-bold text-gray-700">מספר ארגזים</p>
                      <p className="text-gray-900">{request.numberOfBoxes}</p>
                  </div>
                  {request.numberOfBags !== undefined && (
                    <div>
                        <p className="font-bold text-gray-700">מספר באלות</p>
                        <p className="text-gray-900">{request.numberOfBags}</p>
                    </div>
                  )}
                  {request.numberOfClothingBags !== undefined && (
                    <div>
                        <p className="font-bold text-gray-700">מספר שקיות בגדים</p>
                        <p className="text-gray-900">{request.numberOfClothingBags}</p>
                    </div>
                  )}
                  <div>
                      <p className="font-bold text-gray-700">מנוף</p>
                      <p className="text-gray-900">{request.requiresCrane ? 'נדרש' : 'לא נדרש'}</p>
                  </div>
                  
                  {request.heavyFurniture && request.heavyFurniture.length > 0 && (
                      <div>
                          <p className="font-bold text-gray-700">ריהוט כבד</p>
                          <ul className="list-disc list-inside text-gray-900">
                            {request.heavyFurniture.map((item, idx) => (
                                <li key={idx}>
                                    {typeof item === 'string' ? item : `${item.name} (x${item.quantity || 1})`}
                                </li>
                            ))}
                          </ul>
                      </div>
                  )}

                   {request.electricalItems && request.electricalItems.length > 0 && (
                      <div>
                          <p className="font-bold text-gray-700">מוצרי חשמל</p>
                          <ul className="list-disc list-inside text-gray-900">
                            {request.electricalItems.map((item, idx) => (
                                <li key={idx}>
                                    {typeof item === 'string' ? item : `${item.name} (x${item.quantity || 1})`}
                                </li>
                            ))}
                          </ul>
                      </div>
                  )}
              </div>
           </div>
        </div>
        
        {request.additionalNotes && (
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-700 mb-2">הערות נוספות</h4>
                <p className="text-gray-900 whitespace-pre-wrap">{request.additionalNotes}</p>
            </div>
        )}

        <div className="mt-8 flex justify-end">
          <button onClick={onClose} className="btn-secondary px-6 py-2">
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

// Request Card Component
const RequestCard = ({ request, onSubmitOffer, hasExistingOffer }) => {
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!price.trim()) {
      toast.error('יש להזין מחיר');
      return;
    }
    onSubmitOffer(request._id, price, notes);
    setShowOfferForm(false);
    setPrice('');
    setNotes('');
  };

  return (
    <div className="card hover-lift">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-xl font-bold text-brown-900 mb-2">
            הובלה מ{(request.originAddress?.street || request.originAddress || '').replace(/[0-9]/g, '') || 'לא ידוע'} ל{(request.destinationAddress?.street || request.destinationAddress || '').replace(/[0-9]/g, '') || 'לא ידוע'}
          </h3>
          <p className="text-brown-700 mb-1">
            <strong>תאריך:</strong> {request.preferredDate ? (() => {
              try {
                const date = new Date(request.preferredDate);
                return isNaN(date.getTime()) ? 'לא ידוע' : date.toLocaleDateString('he-IL');
              } catch (error) {
                return 'לא ידוע';
              }
            })() : 'לא ידוע'}
          </p>
          <p className="text-brown-700 mb-1">
            <strong>שעה:</strong> לא צוין
          </p>
          <p className="text-brown-700 mb-1">
            <strong>לקוח:</strong> {request.customerName}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {request.category && (
              <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                {request.category}
              </span>
            )}
            {request.requiresCrane && (
              <span className="inline-block bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                נדרש מנוף
              </span>
            )}
          </div>
        </div>
        
        <div className="text-left">
          <p className="text-sm text-brown-600 mb-1">מרחק משוער</p>
          <p className="text-2xl font-bold text-orange-600">
            לא ידוע ק"מ
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <h4 className="font-semibold text-brown-900 mb-2">כתובת המוצא</h4>
          <p className="text-brown-700">{request.originAddress?.street || request.originAddress || 'לא ידוע'}</p>
          <p className="text-brown-600 text-sm">קומה: {request.originAddress?.floor ?? request.originFloor ?? 'לא ידוע'}</p>
        </div>
        
        <div>
          <h4 className="font-semibold text-brown-900 mb-2">כתובת היעד</h4>
          <p className="text-brown-700">{request.destinationAddress?.street || request.destinationAddress || 'לא ידוע'}</p>
          <p className="text-brown-600 text-sm">קומה: {request.destinationAddress?.floor ?? request.destinationFloor ?? 'לא ידוע'}</p>
        </div>
      </div>

      <div className="mb-6 grid md:grid-cols-3 gap-4">
        {request.numberOfBoxes > 0 && (
          <div>
            <h4 className="font-semibold text-brown-900 mb-2">מספר ארגזים</h4>
            <p className="text-brown-700">{request.numberOfBoxes} ארגזים</p>
          </div>
        )}
        {request.numberOfBags !== undefined && request.numberOfBags > 0 && (
          <div>
            <h4 className="font-semibold text-brown-900 mb-2">מספר באלות</h4>
            <p className="text-brown-700">{request.numberOfBags} באלות</p>
          </div>
        )}
        {request.numberOfClothingBags !== undefined && request.numberOfClothingBags > 0 && (
          <div>
            <h4 className="font-semibold text-brown-900 mb-2">מספר שקיות בגדים</h4>
            <p className="text-brown-700">{request.numberOfClothingBags} שקיות</p>
          </div>
        )}
      </div>

      {request.heavyFurniture?.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-brown-900 mb-2">רהיטים כבדים</h4>
          <div className="flex flex-wrap gap-2">
            {request.heavyFurniture.map((item, index) => {
              const itemName = typeof item === 'string' ? item : item.name;
              const quantity = typeof item === 'string' ? 1 : (item.quantity || 1);
              
              return (
                <span key={index} className="bg-brown-100 text-brown-800 text-sm px-3 py-1 rounded-full">
                  {itemName} {quantity > 1 ? `(x${quantity})` : ''}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {request.additionalNotes && (
        <div className="mb-6">
          <h4 className="font-semibold text-brown-900 mb-2">הערות נוספות</h4>
          <p className="text-brown-700">{request.additionalNotes}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm text-brown-600">
          נשלח ב: {new Date(request.createdAt).toLocaleDateString('he-IL')}
        </div>
        
        {hasExistingOffer ? (
          <span className="text-success-600 font-semibold">✓ כבר הגשת הצעה</span>
        ) : (
          <button
            onClick={() => setShowOfferForm(true)}
            className="btn-primary px-6 py-3"
          >
            שלח הצעה
          </button>
        )}
      </div>

      {/* Offer Form Modal */}
      {showOfferForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">שלח הצעה</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="offer-price" className="form-label">מחיר (בשקלים) *</label>
                <input
                  id="offer-price"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="input-field"
                  placeholder="הזן מחיר"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="offer-notes" className="form-label">הערות (אופציונלי)</label>
                <textarea
                  id="offer-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                  className="input-field"
                  placeholder="הערות נוספות..."
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-3 space-x-reverse">
                <button
                  type="button"
                  onClick={() => setShowOfferForm(false)}
                  className="btn-secondary flex-1"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  שלח הצעה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Offer Card Component
const OfferCard = ({ offer, onCancelOffer }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [editPrice, setEditPrice] = useState(offer.price);
  const [editNotes, setEditNotes] = useState(offer.notes || '');

  // Check if offer can be cancelled based on 48-hour rule
  const canCancelOffer = () => {
    // Can cancel pending offers at any time
    if (offer.status === 'pending') {
      return true;
    }

    // Can cancel offers pending re-approval
    if (offer.status === 'pending_reapproval') {
      return true;
    }
    
    // Can cancel accepted offers only within 48 hours
    if (offer.status === 'accepted' && offer.acceptedAt) {
      const acceptedTime = new Date(offer.acceptedAt);
      const now = new Date();
      const hoursSinceAcceptance = (now - acceptedTime) / (1000 * 60 * 60);
      return hoursSinceAcceptance <= 48;
    }
    
    // Cannot cancel rejected or withdrawn offers
    return false;
  };

  // Check if offer can be updated
  const canUpdateOffer = () => {
    // Can update pending offers
    if (offer.status === 'pending') {
      return true;
    }
    // Can update accepted offers if allowed by request modification
    if (offer.status === 'accepted' && offer.canUpdatePrice) {
      return true;
    }
    // Can update offers pending re-approval (in case they want to correct the price again)
    if (offer.status === 'pending_reapproval') {
      return true;
    }
    return false;
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_ENDPOINTS.OFFERS}/${offer._id}`, {
        price: parseFloat(editPrice),
        notes: editNotes
      });
      
      toast.success('ההצעה עודכנה בהצלחה!');
      setShowEditModal(false);
      // We should trigger a refresh here, but for now just close modal
      // In a real app, we'd callback to refresh parent list
      window.location.reload(); // Simple refresh for now to see status changes
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון ההצעה');
    }
  };

  const handleCancel = () => {
    onCancelOffer(offer._id);
    setShowCancelConfirm(false);
  };

  const handleApproveChanges = async () => {
      try {
          await axios.post(`${API_ENDPOINTS.OFFERS}/${offer._id}/approve-changes`);
          toast.success('השינויים אושרו בהצלחה!');
          window.location.reload();
      } catch (error) {
          toast.error(error.response?.data?.message || 'שגיאה באישור השינויים');
      }
  };

  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return 'ריק';
    if (val instanceof Date) return new Date(val).toLocaleDateString('he-IL');
    if (typeof val === 'boolean') return val ? 'כן' : 'לא';
    
    if (Array.isArray(val)) {
      if (val.length === 0) return 'ללא';
      // Normalize and sort array items for consistent comparison
      const normalizedItems = val.map(item => {
        if (typeof item === 'string') {
          return { type: 'string', value: item, display: item };
        }
        if (typeof item === 'object' && item !== null) {
          const name = item.name || item.label || 'פריט';
          const quantity = item.quantity || 1;
          return {
            type: 'object',
            name,
            quantity,
            display: quantity > 1 ? `${name} (x${quantity})` : name
          };
        }
        const str = String(item);
        return { type: 'string', value: str, display: str };
      }).sort((a, b) => {
        // Sort by type first (strings before objects), then by value
        if (a.type !== b.type) {
          return a.type === 'string' ? -1 : 1;
        }
        if (a.type === 'string') {
          return a.value.localeCompare(b.value);
        }
        // Both are objects
        if (a.name !== b.name) {
          return a.name.localeCompare(b.name);
        }
        return a.quantity - b.quantity;
      });
      
      return normalizedItems.map(item => item.display).join(', ');
    }

    if (typeof val === 'object' && val !== null) {
        if (val.name) {
             const quantity = val.quantity || 1;
             return quantity > 1 ? `${val.name} (x${quantity})` : val.name;
        }
        if (val.street) {
            return `${val.street} ${val.number || ''}, ${val.city || ''}`.trim().replace(/,\s*$/, '').replace(/^\s*,/, '');
        }
        try {
            return JSON.stringify(val);
        } catch (e) {
            return 'אובייקט';
        }
    }
    
    return String(val);
  };

  // Filter out changes where formatted oldValue and newValue are identical
  const getActualChanges = () => {
    if (!offer.pendingChanges || offer.pendingChanges.length === 0) return [];
    return offer.pendingChanges.filter(change => {
      const oldFormatted = formatValue(change.oldValue);
      const newFormatted = formatValue(change.newValue);
      return oldFormatted !== newFormatted;
    });
  };

  const getStatusText = () => {
    switch (offer.status) {
      case 'pending':
        return 'ממתין לתגובה';
      case 'accepted':
        return 'התקבל';
      case 'rejected':
        return 'נדחה';
      case 'withdrawn':
        return 'בוטל';
      case 'date_unavailable':
        return 'תאריך זה נלקח';
      case 'pending_reapproval':
        return 'ממתין לאישור מחדש';
      default:
        return 'לא ידוע';
    }
  };

  const getStatusColor = () => {
    switch (offer.status) {
      case 'pending':
        return 'text-yellow-600';
      case 'accepted':
        return 'text-green-600';
      case 'rejected':
        return 'text-red-600';
      case 'withdrawn':
        return 'text-gray-600';
      case 'date_unavailable':
        return 'text-orange-600';
      case 'pending_reapproval':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className={`card hover-lift ${offer.status === 'date_unavailable' ? 'border-2 border-orange-200 bg-orange-50 opacity-75' : ''}`}>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-xl font-bold text-brown-900 mb-2 flex items-center gap-2">
            הצעה על הובלה מ{
              offer.request?.originAddress?.street || offer.request?.originAddress
                ? (offer.request.originAddress.street || offer.request.originAddress).replace(/[0-9]/g, '') || 'לא ידוע'
                : 'לא ידוע'
            } ל{
              offer.request?.destinationAddress?.street || offer.request?.destinationAddress
                ? (offer.request.destinationAddress.street || offer.request.destinationAddress).replace(/[0-9]/g, '') || 'לא ידוע'
                : 'לא ידוע'
            }
             <button 
                onClick={() => setShowRequestDetails(true)}
                className="text-orange-600 hover:text-orange-700 transition-colors"
                title="צפה בפרטי הבקשה המלאים"
             >
               <Eye className="h-5 w-5" />
             </button>
          </h3>
          <p className="text-sm text-gray-500 mb-2">
             מספר בקשה: {offer.request?.requestId || 'לא ידוע'}
          </p>
          <p className="text-brown-700 mb-1">
            <strong>תאריך הובלה:</strong> {offer.request?.preferredDate ? (() => {
              try {
                const date = new Date(offer.request.preferredDate);
                return isNaN(date.getTime()) ? 'לא ידוע' : date.toLocaleDateString('he-IL');
              } catch (error) {
                return 'לא ידוע';
              }
            })() : 'לא ידוע'}
          </p>
          <p className="text-brown-700 mb-1">
            <strong>לקוח:</strong> {offer.request?.customerName || 'לא ידוע'}
          </p>
          {/* Customer phone only visible after 48h from acceptance of this offer */}
          {offer.phoneRevealAllowed && offer.customerPhone ? (
            <p className="text-green-700 mb-1">
              <strong>טלפון הלקוח:</strong> <a className="underline" href={`tel:${offer.customerPhone}`}>{offer.customerPhone}</a>
            </p>
          ) : (
            <p className="text-sm text-brown-600">
              מספרי הטלפון ייחשפו לאחר מעבר חלון הביטול (48 שעות מקבלת ההצעה)
            </p>
          )}
          <p className={`text-sm font-semibold ${getStatusColor()}`}>
            <strong>סטטוס:</strong> {getStatusText()}
          </p>
        </div>
        
        <div className="text-left">
          <p className="text-sm text-brown-600 mb-1">המחיר שלך</p>
          <p className={`text-3xl font-bold ${offer.status === 'date_unavailable' ? 'text-orange-400 line-through' : 'text-orange-600'}`}>
            ₪{offer.price}
          </p>
        </div>
      </div>

      {/* Pending Changes Alert */}
      {offer.status === 'pending_reapproval' && (() => {
        const actualChanges = getActualChanges();
        if (actualChanges.length === 0) return null;
        
        return (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="flex-grow">
                      <h4 className="font-bold text-blue-800 mb-2">הלקוח עדכן את פרטי הבקשה</h4>
                      <p className="text-sm text-blue-700 mb-3">יש לאשר את השינויים כדי להמשיך בתהליך. ניתן לעדכן את המחיר במידת הצורך.</p>
                      
                      <div className="bg-white rounded border border-blue-100 p-3 mb-3">
                          <h5 className="font-semibold text-blue-900 text-sm mb-2">מה השתנה:</h5>
                          <ul className="space-y-2 text-sm">
                              {actualChanges.map((change, idx) => (
                                  <li key={idx} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                      <span className="font-medium text-gray-700">{change.label || change.field}:</span>
                                      <div className="flex items-center gap-2 flex-wrap">
                                          <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded line-through text-xs">
                                            {formatValue(change.oldValue)}
                                          </span>
                                          <span className="text-gray-400">➜</span>
                                          <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                                            {formatValue(change.newValue)}
                                          </span>
                                      </div>
                                  </li>
                              ))}
                          </ul>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-4">
                          <button onClick={handleApproveChanges} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm font-medium flex items-center">
                              <Check className="h-4 w-4 ml-2" />
                              אשר ללא שינוי במחיר
                          </button>
                          <button onClick={() => setShowEditModal(true)} className="bg-white text-blue-600 border border-blue-300 px-4 py-2 rounded hover:bg-blue-50 transition text-sm font-medium">
                              עדכן מחיר ואשר
                          </button>
                      </div>
                  </div>
              </div>
          </div>
        );
      })()}

      {offer.status === 'date_unavailable' && (
        <div className="mb-6 p-3 bg-orange-100 rounded-lg border border-orange-200">
          <p className="text-orange-800 font-semibold text-sm">
            תאריך זה נלקח
          </p>
          <p className="text-orange-700 text-xs mt-1">
            קיבלת הזמנה אחרת לתאריך זה, ולכן הצעה זו לא רלוונטית יותר
          </p>
        </div>
      )}

      {offer.notes && (
        <div className="mb-6">
          <h4 className="font-semibold text-brown-900 mb-2">הערות שלך</h4>
          <p className="text-brown-700">{offer.notes}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm text-brown-600">
          נשלח ב: {new Date(offer.createdAt).toLocaleDateString('he-IL')}
        </div>
        
        <div className="flex gap-2">
            {canCancelOffer() && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="btn-danger px-4 py-2 flex items-center text-sm"
              >
                <X className="h-4 w-4 ml-1" />
                בטל
              </button>
            )}
            
            {/* Edit Button for Updatable Offers */}
            {canUpdateOffer() && (
              <button
                onClick={() => setShowEditModal(true)}
                className="btn-secondary px-4 py-2 flex items-center text-sm"
              >
                ערוך
              </button>
            )}
        </div>
      </div>

      {/* Modals */}
      <RequestDetailsModal 
        request={offer.request} 
        isOpen={showRequestDetails} 
        onClose={() => setShowRequestDetails(false)} 
      />

      {/* Edit Offer Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">ערוך הצעה</h3>
            {offer.status === 'pending_reapproval' && (
                <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded text-sm">
                    עדכון המחיר יאשר אוטומטית את השינויים בבקשה.
                </div>
            )}
            <div className="space-y-6">
              <div>
                <label htmlFor="edit-price" className="form-label">מחיר (בשקלים) *</label>
                <input
                  id="edit-price"
                  type="number"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="input-field"
                  placeholder="הזן מחיר"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label htmlFor="edit-notes" className="form-label">הערות (אופציונלי)</label>
                <textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  maxLength={500}
                  className="input-field"
                  rows={3}
                />
              </div>
              <div className="flex space-x-3 space-x-reverse">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary flex-1"
                >
                  ביטול
                </button>
                <button
                  onClick={handleUpdate}
                  className="btn-primary flex-1"
                >
                  עדכן הצעה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">בטל הצעה</h3>
            <p className="text-brown-700 mb-6 text-center">
              האם אתה בטוח שברצונך לבטל את ההצעה? פעולה זו לא ניתנת לביטול.
            </p>
            
            <div className="flex space-x-3 space-x-reverse">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                className="btn-secondary flex-1"
              >
                ביטול
              </button>
              <button
                onClick={handleCancel}
                className="btn-danger flex-1"
              >
                בטל הצעה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboardPage;